import { useEffect, useState } from "react";
import { getWordPressConnectionStatus, listWordPressSites, type WordPressSiteOption } from "@/lib/composerCore";

/** Tracks WordPress.com gateway link status + on-demand site list for the
 *  connector editor. Resets whenever the editor closes or switches connector
 *  type away from `wordpress`. */
export const useWordPressGateway = (
  workspaceId: string,
  open: boolean,
  connectorType: string,
) => {
  const [wpLinked, setWpLinked] = useState<boolean | null>(null);
  const [wpStatusLoading, setWpStatusLoading] = useState(false);
  const [wpSites, setWpSites] = useState<WordPressSiteOption[] | null>(null);
  const [wpSitesLoading, setWpSitesLoading] = useState(false);
  const [wpSitesError, setWpSitesError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || connectorType !== "wordpress") {
      setWpLinked(null);
      setWpStatusLoading(false);
      setWpSites(null);
      setWpSitesError(null);
      setWpSitesLoading(false);
      return;
    }
    let cancelled = false;
    setWpStatusLoading(true);
    getWordPressConnectionStatus(workspaceId)
      .then((res) => {
        if (cancelled) return;
        setWpLinked(res.linked);
      })
      .catch(() => {
        if (cancelled) return;
        setWpLinked(false);
      })
      .finally(() => {
        if (cancelled) return;
        setWpStatusLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, connectorType, workspaceId]);

  const loadWpSites = async () => {
    setWpSitesError(null);
    setWpSitesLoading(true);
    try {
      const sites = await listWordPressSites(workspaceId);
      setWpSites(sites);
      if (sites.length === 0) {
        setWpSitesError("No WordPress.com sites found on the linked account.");
      }
    } catch (err) {
      const message = (err as Error).message ?? "Could not load WordPress.com sites";
      setWpSitesError(message);
    } finally {
      setWpSitesLoading(false);
    }
  };

  return { wpLinked, wpStatusLoading, wpSites, wpSitesLoading, wpSitesError, loadWpSites };
};
